import type {
  TClassOfUnitObject,
  TDictionarySettings,
  TUnitColumnBinding,
  TUnitSettingItem,
  TUomObject,
} from './types';
import type { TControlView } from 'src/services/directory-service/types';

export const createUnitColumnBindings = (fieldViews: TControlView[]): TUnitColumnBinding[] => {
  return fieldViews.reduce<TUnitColumnBinding[]>((bindings, view) => {
    const classOfUnitSlug = view.classOfUnitSlug?.trim();

    if (!classOfUnitSlug) {
      return bindings;
    }

    bindings.push({
      fieldId: view.fieldId,
      label: view.defaultLabel || view.fieldId,
      classOfUnitSlug,
    });

    return bindings;
  }, []);
};

export const createUnitSettingItems = (
  bindings: TUnitColumnBinding[],
  settings: TDictionarySettings,
  classesOfUnit: ReadonlyArray<TClassOfUnitObject>,
  units: ReadonlyArray<TUomObject>,
): TUnitSettingItem[] => {
  const classesBySlug = new Map(
    classesOfUnit
      .filter(({ status }) => status === 'ACTIVE')
      .flatMap((classOfUnit) => {
        const slug = classOfUnit.data.slug?.trim();

        return slug ? [[slug, classOfUnit] as const] : [];
      }),
  );
  const unitsById = new Map(units.filter(({ status }) => status === 'ACTIVE').map((unit) => [unit.id, unit]));

  return bindings.map((binding) => {
    const fieldSetting = settings.fields?.[binding.fieldId];
    const classOfUnit = classesBySlug.get(binding.classOfUnitSlug);
    const classOfUnitId = typeof fieldSetting?.classOfUnitId === 'number' ? fieldSetting.classOfUnitId : null;
    const isClassMatched = !!classOfUnit && classOfUnit.id === classOfUnitId;
    const options = isClassMatched
      ? (classOfUnit.data.unitIds ?? []).flatMap((unitId) => {
          const abbreviation = unitsById.get(unitId)?.data.abbreviation?.trim();

          return abbreviation ? [{ label: abbreviation, value: unitId }] : [];
        })
      : [];
    const savedUomId = typeof fieldSetting?.uomId === 'number' ? fieldSetting.uomId : null;
    const value = options.some((option) => option.value === savedUomId) ? savedUomId : null;

    return {
      ...binding,
      classOfUnitId,
      value,
      displayValue: value,
      options,
      isDisabled: !isClassMatched || !options.length,
      isSaving: false,
      hasError: false,
      errorMessage: null,
    };
  });
};
